import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, Loader2, CheckCircle2, AlertCircle, PawPrint, Share2 } from "lucide-react";
import SEO from "../components/SEO";

/**
 * Post-checkout landing page.
 * - Stripe redirects here with ?session_id=cs_test_...
 * - Polls /api/donations/status/{session_id} up to 10 times (every 2s)
 * - When paid, renders the dedication (if any) and a share-the-wall CTA
 */
export default function DonateThanks() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({
    phase: sessionId ? "checking" : "missing", // checking | paid | failed | missing | timeout
    data: null,
    attempts: 0,
  });

  const poll = useCallback(
    async (attempts = 0) => {
      if (!sessionId) return;
      try {
        const r = await api.get(`/donations/status/${sessionId}`);
        const d = r.data;
        if (d.payment_status === "paid") {
          setState({ phase: "paid", data: d, attempts });
          return;
        }
        if (d.status === "expired" || d.status === "canceled") {
          setState({ phase: "failed", data: d, attempts });
          return;
        }
        if (attempts >= 9) {
          setState({ phase: "timeout", data: d, attempts });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (err) {
        if (attempts >= 9) {
          setState({ phase: "failed", data: null, attempts });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (sessionId) poll(0);
  }, [poll, sessionId]);

  const ded = state.data?.dedication;
  const hasDedication = ded && (ded.pet_name || ded.message);

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16" data-testid="donate-thanks-page">
      <SEO title="Thank You · Donation Received" path="/donate/thanks" noindex />

      {state.phase === "checking" && (
        <div className="text-center" data-testid="donate-thanks-checking">
          <Loader2 size={32} className="animate-spin mx-auto text-red-600" />
          <h1 className="font-display font-black text-3xl tracking-tight mt-6">
            Confirming your donation…
          </h1>
          <p className="text-zinc-600 mt-3">This usually takes a few seconds.</p>
        </div>
      )}

      {state.phase === "paid" && (
        <div data-testid="donate-thanks-paid">
          <div className="text-center">
            <div className="inline-flex w-16 h-16 items-center justify-center bg-red-600 text-white border-2 border-zinc-900">
              <Heart size={28} className="fill-white" />
            </div>
            <div className="label-eyebrow text-red-600 mt-6">Donation received</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 leading-tight">
              Thank you.
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">You just kept the lights on.</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-red-600/90 -z-0" />
              </span>
            </h1>
            <p className="text-zinc-700 mt-5 max-w-xl mx-auto leading-relaxed">
              Your{" "}
              <strong>
                {state.data?.type === "monthly" ? "monthly sponsorship" : "gift"}
              </strong>{" "}
              of{" "}
              <strong>
                $
                {state.data?.amount_total
                  ? (state.data.amount_total / 100).toFixed(2)
                  : "—"}
              </strong>{" "}
              is processed. Every dollar funds hosting, the LLM ingestor, geocoding,
              and legal review — so adopters, fosters and rescues can keep checking
              this registry, free, forever.
            </p>
          </div>

          {hasDedication && (
            <div
              className="mt-10 border-2 border-zinc-900 bg-amber-50 p-6 sm:p-8"
              data-testid="donate-thanks-dedication"
            >
              <div className="flex items-start gap-4">
                <div className="bg-zinc-900 text-white p-3 flex-shrink-0">
                  <PawPrint size={20} />
                </div>
                <div className="flex-1">
                  <div className="label-eyebrow text-red-600">In memory of</div>
                  <div className="font-display font-black text-3xl tracking-tighter mt-1">
                    {ded.pet_name || "(unnamed)"}
                  </div>
                  {ded.message && (
                    <blockquote className="border-l-4 border-red-600 pl-4 mt-4 text-zinc-800 italic leading-relaxed">
                      &ldquo;{ded.message}&rdquo;
                    </blockquote>
                  )}
                  {ded.donor_name && (
                    <div className="mt-3 text-xs uppercase tracking-widest font-mono text-zinc-600">
                      — {ded.donor_name}
                    </div>
                  )}
                  {ded.show_publicly && (
                    <div className="mt-4 text-xs text-zinc-600 inline-flex items-center gap-2">
                      <Share2 size={12} />
                      This dedication is now on the public memorial wall.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {hasDedication && ded.show_publicly && (
              <Link
                to="/in-memory"
                className="bg-zinc-900 text-white border-2 border-zinc-900 hover:bg-red-600 hover:border-red-600 px-6 py-3 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 transition-colors"
                data-testid="thanks-view-wall"
              >
                <PawPrint size={14} /> See the memorial wall
              </Link>
            )}
            <Link
              to="/"
              className="border-2 border-zinc-900 px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2 transition-colors"
              data-testid="thanks-back-home"
            >
              Back to registry
            </Link>
          </div>
        </div>
      )}

      {state.phase === "failed" && (
        <div className="text-center" data-testid="donate-thanks-failed">
          <AlertCircle size={32} className="mx-auto text-red-600" />
          <h1 className="font-display font-black text-3xl tracking-tight mt-6">
            We couldn&apos;t confirm your donation.
          </h1>
          <p className="text-zinc-700 mt-4 max-w-md mx-auto">
            If your card was charged, your receipt will arrive by email. Otherwise no
            charge was made. You can try again or contact{" "}
            <a href="mailto:info@pawsonrecord.com" className="underline text-red-600">
              info@pawsonrecord.com
            </a>
            .
          </p>
          <Link to="/donate" className="btn-primary mt-6 inline-flex" data-testid="thanks-try-again">
            Try again
          </Link>
        </div>
      )}

      {state.phase === "timeout" && (
        <div className="text-center" data-testid="donate-thanks-timeout">
          <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
          <h1 className="font-display font-black text-3xl tracking-tight mt-6">
            Still processing…
          </h1>
          <p className="text-zinc-700 mt-4 max-w-md mx-auto">
            Stripe is taking a bit longer than usual. Your receipt will land in your
            inbox shortly. If anything looks wrong, email{" "}
            <a href="mailto:info@pawsonrecord.com" className="underline text-red-600">
              info@pawsonrecord.com
            </a>
            .
          </p>
          <Link to="/" className="btn-outline mt-6 inline-flex" data-testid="thanks-back-home-timeout">
            Back to registry
          </Link>
        </div>
      )}

      {state.phase === "missing" && (
        <div className="text-center" data-testid="donate-thanks-missing">
          <h1 className="font-display font-black text-3xl tracking-tight">
            No donation session found.
          </h1>
          <p className="text-zinc-700 mt-4">
            Try the donate page to make a contribution.
          </p>
          <Link to="/donate" className="btn-primary mt-6 inline-flex">
            Donate
          </Link>
        </div>
      )}
    </div>
  );
}
